import React from 'react';
import { useI18n } from '../i18n';

// Hard-coded, non-AI safety routing screen. Shown whenever the teacher's text
// trips the risk-language screen (see src/safety.mjs) or chooses the crisis path.
// It makes NO model call and gives NO advice on what to say to the student — it
// routes to human help only. Nothing about the student is logged here.
//
// Crisis resources are localized per language (see i18n locales → safety). A
// specific hotline box shows only when that locale has a verified number; where
// it does not (e.g. Spanish spans many countries), it shows the local-emergency
// line plus an international directory instead of an invented number.

interface Props {
  onBack?: () => void; // return to the previous screen so the teacher can rephrase
}

const box: React.CSSProperties = {
  background: 'var(--white)', border: '1px solid #e7e0d6', borderRadius: 12,
  padding: '16px 18px', marginBottom: 12, display: 'flex', gap: 14, alignItems: 'flex-start',
};
const label: React.CSSProperties = { fontWeight: 700, color: 'var(--charcoal)', marginBottom: 4, fontSize: 15 };
const sub: React.CSSProperties = { fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)' };

export default function SafetyInterstitial({ onBack }: Props) {
  const { t } = useI18n();
  const lifelineTel = t('safety.lifelineTel');
  const dangerTel = t('safety.dangerTel');
  const internationalBody = t('safety.internationalBody');

  return (
    <div className="page-content animate-in" style={{ paddingTop: 24, paddingBottom: 40 }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <p style={{ fontSize: 34, marginBottom: 8 }}>🛟</p>
        <h2 style={{ marginBottom: 8 }}>{t('safety.heading')}</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6, maxWidth: 460, margin: '0 auto' }}>
          {t('safety.intro')}
        </p>
      </div>

      <div style={box}>
        <span style={{ fontSize: 22 }}>🏫</span>
        <div>
          <p style={label}>{t('safety.schoolLabel')}</p>
          <p style={sub}>{t('safety.schoolBody')}</p>
        </div>
      </div>

      {lifelineTel && (
        <div style={box}>
          <span style={{ fontSize: 22 }}>💬</span>
          <div>
            <p style={label}>{t('safety.lifelineLabel')}</p>
            <p style={sub}>{t('safety.lifelineBody', { tel: lifelineTel }).split(lifelineTel).flatMap((part, i, arr) => i < arr.length - 1 ? [part, <a key={i} href={`tel:${lifelineTel}`} style={{ color: 'var(--teal)', fontWeight: 700 }}>{lifelineTel}</a>] : [part])}</p>
          </div>
        </div>
      )}

      <div style={box}>
        <span style={{ fontSize: 22 }}>🚨</span>
        <div>
          <p style={label}>{t('safety.dangerLabel')}</p>
          <p style={sub}>{t('safety.dangerBody', { tel: dangerTel }).split(dangerTel).flatMap((part, i, arr) => i < arr.length - 1 ? [part, <a key={i} href={`tel:${dangerTel}`} style={{ color: 'var(--coral)', fontWeight: 700 }}>{dangerTel}</a>] : [part])}</p>
        </div>
      </div>

      {internationalBody && (
        <div style={box}>
          <span style={{ fontSize: 22 }}>🌐</span>
          <div>
            <p style={label}>{t('safety.internationalLabel')}</p>
            <p style={sub}>
              {internationalBody.split('findahelpline.com').flatMap((part, i, arr) => i < arr.length - 1 ? [part, <a key={i} href="https://findahelpline.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)', fontWeight: 700 }}>findahelpline.com</a>] : [part])}
            </p>
          </div>
        </div>
      )}

      <div style={{ ...box, background: 'var(--amber-light)', border: '1px solid var(--amber)' }}>
        <span style={{ fontSize: 22 }}>📋</span>
        <div>
          <p style={label}>{t('safety.reporterLabel')}</p>
          <p style={sub}>{t('safety.reporterBody')}</p>
        </div>
      </div>

      {onBack && (
        <button onClick={onBack} className="btn-secondary" style={{ marginTop: 12 }}>
          {t('common.back')}
        </button>
      )}
    </div>
  );
}
